import { Popover } from '@foam/buoy/src/components/Popover/Popover';
import { SOCIAL_PLATFORMS } from '~/constants';
import { SupportedPlatforms } from '~/constants/platforms';

const metricsInfo = {
  instagram: [
    {
      title: 'Average Reach',
      description:
        'Average number of unique accounts that have seen the reels, stories and posts.',
    },
    {
      title: 'Average Views',
      description:
        "Average number of times the Talent's reels, stories and posts have been seen.",
    },
    {
      title: 'Average Engagements',
      description:
        'Average number of likes, comments, saves, and shares on each reel and post.',
    },
    {
      title: 'Follower Engagement Rate',
      description:
        'Percentage of followers who have interacted with the reels and posts - out of the total follower count.',
    },
    {
      title: 'Reach Engagement Rate',
      description:
        'Average percentage of unique accounts who have seen and engaged with the reels and posts.',
    },
    {
      title: 'View Engagement Rate',
      description:
        'Average percentage of engagements to total views of the reels and posts.',
    },
    {
      title: 'Average Reels Views',
      description: 'Average number of video sessions on the reels.',
    },
    {
      title: 'Average Story Reach',
      description:
        "Average number of unique accounts that have seen the Talent's stories.",
    },
    {
      title: 'Average Story Views',
      description:
        "Average number of times the Talent's stories have been seen.",
    },
  ],
  tiktok: [
    {
      title: 'Average Views',
      description:
        "Average number of times each of the Talent's videos has been viewed.",
    },
    {
      title: 'Average Engagements',
      description: 'Average number of likes, comments and shares.',
    },
    {
      title: 'Average Likes',
      description: "Average number of likes on the Talent's posts.",
    },
    {
      title: 'Average Comments',
      description: "Average number of comments on the Talent's posts.",
    },
    {
      title: 'Average Shares',
      description: "Average number of shares of the Talent's posts.",
    },
    {
      title: 'View Engagement Rate',
      description: 'Average engagements divided by the average video views.',
    },
  ],
  youtube: [
    {
      title: 'Average Views',
      description:
        "Average number of times each of the Talent's videos has been viewed.",
    },
    {
      title: 'Average Likes',
      description:
        "Average number of times each of the Talent's videos has been liked.",
    },
    {
      title: 'Average Comments',
      description:
        "Average number of times each of the Talent's videos has been commented on.",
    },
    {
      title: 'Average Shares',
      description:
        "Average number of times each of the Talent's videos has been shared.",
    },
  ],
  snapchat: [
    {
      title: 'Story Average View Time',
      description:
        "Average time spent watching a Talent's public stories before moving to the next snap (in milliseconds).",
    },
    {
      title: 'Saved Story Average View Time',
      description:
        "Average time spent watching a Talent's saved stories (permanent stories on their profile) in milliseconds.",
    },
    {
      title: 'Spotlight Average View Time',
      description:
        "Average time spent watching a Talent's spotlight videos before swiping away (in milliseconds).",
    },
  ],
};

const infoMetricsTitle = {
  instagram:
    'Averages are calculated based on Instagram data from the last 30 days.',
  tiktok:
    'Averages are calculated based on TikTok data from the last 30 videos.',
  youtube: "Averages are pulled directly from YouTube's API.",
  snapchat:
    "Averages are pulled directly from Snapchat's API, and are based on the last 28 days.",
};

type Placement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

interface Props {
  platform: SupportedPlatforms;
  placement?: Placement;
  flip?: boolean;
}

export const MetricsInfoPopover: React.FC<Props> = ({
  platform = SOCIAL_PLATFORMS.instagram,
  placement = 'bottom-end',
  flip = true,
}) => (
  <Popover.Instance placement={placement}>
    <Popover.Trigger>
      <button
        type="button"
        className="text-14 font-400 text-blue-500 hover:underline"
      >
        Learn more about metrics
      </button>
    </Popover.Trigger>
    <Popover.Root size="sm" flip={flip}>
      <Popover.Heading>Metrics data</Popover.Heading>
      <div className="flex items-center gap-1">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          fill="currentColor"
          className="size-4 shrink-0 text-neutral-400"
        >
          <path
            fillRule="evenodd"
            d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm.75-10.25a.75.75 0 0 0-1.5 0v1.5a.75.75 0 0 0 1.5 0v-1.5ZM8 8a.75.75 0 0 0-.75.75v2.5a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8 8Z"
            clipRule="evenodd"
          />
        </svg>
        <p className="text-12 font-500 text-neutral-400">
          {infoMetricsTitle[platform]}
        </p>
      </div>
      <div className="mt-2 flex flex-col gap-1">
        {metricsInfo[platform].map((metric) => (
          <div key={metric.title}>
            <p className="text-14 font-500 text-neutral-700">{metric.title}</p>
            <p className="text-14 text-neutral-700">{metric.description}</p>
          </div>
        ))}
      </div>
    </Popover.Root>
  </Popover.Instance>
);
